/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {StatefulComponent} from '../../../shared/stateful/stateful.component';
import {ProxyManagementState} from './proxy-management.state';
import {ChipInputTypeEnum} from '../../../shared/chip-input/models/chip-input-type.enum';
import {ChipInputConfigurationModel} from '../../../shared/chip-input/models/chip-input-configuration.model';
import {objectsValidator} from '../../../shared/input/input-validators';
import {ChipInputAutocompleteConfigurationModel} from '../../../shared/chip-input/models/chip-input-autocomplete-configuration.model';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {UserDetailModel} from '../../models/user/user-detail/user-detail.model';
import {GroupTypeEnum} from '../../../api/ms-security/contracts/group-type.enum';
import {concatMap, finalize} from 'rxjs/operators';
import {UserDetailMapper} from '../../models/user/user-detail/user-detail.mapper';
import {ChipInputValueModel} from '../../../shared/chip-input/models/chip-input-value.model';
import {UserProxiesCommandContract} from '../../../api/ms-partnumberchanges/contracts/user/user-proxies-command.contract';
import {UserApi} from '../../../api/ms-partnumberchanges/user.api';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {SpinnerService} from '../../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../../shared/spinner/spinner-context-type.enum';

@Component({
    selector: 'app-proxy-management',
    templateUrl: './proxy-management.component.html',
    styleUrls: ['./proxy-management.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProxyManagementComponent extends StatefulComponent<ProxyManagementState> implements OnInit, OnDestroy {
    @Output() proxyListUpdated = new EventEmitter<void>();

    public ChipInputTypeEnum = ChipInputTypeEnum;
    // Chip input configuration for proxy management
    public proxyInputConfiguration: ChipInputConfigurationModel<UserDetailModel> = {
        initialChips: [],
        excludedChipValues: [],
        chipLabel: 'proxy',
        groupLabel: 'proxy',
        inputLabel: 'Be a proxy of',
        chipInputValidator: [objectsValidator],
        chipLabelTransform: this.chipLabelTransform,
        chipSortFunction: this.chipSortFunction,
        placeholder: 'Find a proxy'
    };
    // Chip input autocomplete configuration for proxy management
    public autocompleteConfiguration: ChipInputAutocompleteConfigurationModel<UserDetailModel> = {
        resultFormatter: this.resultAndInputFormatter,
        inputFormatter: this.resultAndInputFormatter,
        resultsFunction: (term: string): Observable<UserDetailModel[]> => {
            if (term != null && term.length > 0) {
                return this.userPncApi.getAutocompleteProxies(term, this.excludedUsernames)
                        .pipe(concatMap((value) => of(UserDetailMapper
                                .fromUserDetailContractArray(value.usersPage))));
            }
            return of([]);
        },
    };
    public addRequestEnd: Subject<boolean> = new Subject<boolean>();
    public deleteRequestEnd: Subject<boolean> = new Subject<boolean>();
    public readonly PROXY_USER_DOES_NOT_EXIST_TOOLTIP = 'The user data does not exist';
    public readonly PROXY_USER_CONNECTION_PROBLEM_TOOLTIP = 'The user data could not be retrieved due to a connection problem';

    // Subscriptions variable
    private subscriptions: Subscription = new Subscription();
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'PROXY_MANAGEMENT_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);

    /**
     * Returns the arrow CSS class for the toggle button
     */
    get toggleButtonArrowClass(): string {
        const base = 'toggle-button-arrow';
        return `${base} ${this.state.isOpen ? 'iwp-icon-gen_arrow_down active' : 'iwp-icon-gen_arrow_right'}`;
    }

    /**
     * Returns the global CSS class for the toggle button
     */
    get toggleButtonClass(): string {
        let base = 'proxy-management-toggle-button';
        if (this.state.isOpen) {
            base = base + ` active`;
        }
        return base;
    }

    /**
     * Return a string[] of proxies already associated with the user
     */
    get excludedUsernames(): string[] {
        return this.state.currentProxies?.map((proxy) => proxy.username);
    }

    get userNoDetailTooltip(): string {
        let tooltip = this.PROXY_USER_DOES_NOT_EXIST_TOOLTIP;
        if (this.state.partialProxyDetailInfo) {
            tooltip = this.PROXY_USER_CONNECTION_PROBLEM_TOOLTIP;
        }
        return tooltip;
    }

    constructor(private userPncApi: UserApi,
                private toast: ToastWrapperService,
                private spinnerService: SpinnerService) {
        super();
    }

    ngOnInit(): void {
        this.storeState({isOpen: false});
    }

    /**
     * Opens and close the proxy management component
     */
    toggleProxyManagement(): void {
        this.storeState({isOpen: !this.state.isOpen});
        if (this.state.isOpen) {
            this.getUserProxies();
        } else {
            if (this.proxiesChanged()) {
                this.proxyListUpdated.emit();
            }
            this.resetProxiesValues();
        }
    }

    /**
     * Retrieves the list of supplying packing plants codes associated with an user
     * @param user {@link UserDetailModel} the user from each to retrieve the supplying packing plant codes list
     * @return string[] the supplying plant codes associated with the user
     */
    retrieveSupplyingPackingPlantsFromUser(user: UserDetailModel): string | null {
        const plants = user?.groups?.filter((group) => group.type === GroupTypeEnum.PLANNER)
                .map((value) => value.code);
        return plants?.join(', ');
    }

    /**
     * Updates the currentProxies value
     * @param chipInputValue {@link ChipInputValueModel<UserDetailModel>} of the current proxies
     */
    updateCurrentUserProxies(chipInputValue: ChipInputValueModel<UserDetailModel>): void {
        this.storeState({currentProxies: chipInputValue?.chips});
    }

    /**
     * Executes de BE request to add a proxy
     * @param proxy {@link UserDetailModel} the proxy to add
     */
    addProxy(proxy: UserDetailModel): void {
        if (proxy?.username != null) {
            this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
            const proxiesCommand: UserProxiesCommandContract = {usernames: [proxy.username]};
            this.subscriptions.add(this.userPncApi.addProxies(proxiesCommand)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                    }))
                    .subscribe(() => {
                        this.toast.success(`You became a proxy of <strong>${proxy.firstName} ${proxy.lastName}</strong> `
                            + `successfully.`,'Success');
                        this.addRequestEnd.next(true);
                    }, () => {
                        this.toast.error(
                                `You were not able to became a proxy of <strong>${proxy.firstName} ${proxy.lastName}</strong> `
                                + `because of an unexpected error.`, 'Error');
                        this.addRequestEnd.next(false);
                    }));
        }
    }

    /**
     * Executes de BE request to remove a proxy
     * @param proxy {@link UserDetailModel} the proxy to add
     */
    deleteProxy(proxy: UserDetailModel): void {
        if (proxy?.username != null) {
            this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
            this.subscriptions.add(this.userPncApi.deleteProxy(proxy.username)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                    }))
                    .subscribe(() => {
                        this.toast.success(
                                `You excluded <strong>${proxy.firstName} ${proxy.lastName}</strong> ` +
                                `as a colleague you are a proxy of successfully.`,
                                'Success');
                        this.deleteRequestEnd.next(true);
                    }, () => {
                        this.toast.error(`You were not able to exclude <strong>${proxy.firstName} ${proxy.lastName}</strong> ` +
                                `as a colleague you are a proxy of because of an unexpected error.`, 'Error');
                        this.deleteRequestEnd.next(false);
                    }));
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    retrieveUserIdentificationLabel(user: UserDetailModel): string {
        let label = null;
        if (user != null) {
            if (user.firstName != null && user.lastName != null) {
                label = `${user.firstName} ${user.lastName} (${user.username})`;
            } else {
                label = user.username;
            }
        }
        return label;
    }

    chipLabelTransform(user: UserDetailModel): string {
        let label = null;
        if (user != null) {
            if (user.firstName != null && user.lastName != null) {
                label = `${user.firstName} ${user.lastName}`;
            } else {
                label = user.username;
            }
        }
        return label;
    }

    hasProxyDetailInfo(proxy: UserDetailModel): boolean {
        return proxy?.firstName != null && proxy?.lastName != null;
    }

    private resetProxiesValues(): void {
        // Reset proxy management state
        this.storeState({
            currentProxies: [],
            initialProxies: []
        });
        // Reset chip-input configuration
        this.proxyInputConfiguration = {
            ...this.proxyInputConfiguration,
            initialChips: this.state.currentProxies
        };
    }

    private chipSortFunction(a: UserDetailModel, b: UserDetailModel): number {
        let textA;
        let textB;
        let sortValue: number = null;

        if (a.firstName != null && a.lastName != null) {
            textA = (`${a.firstName} ${a.lastName}`).toUpperCase();
        } else {
            textA = a.username.toUpperCase();
        }

        if (b.firstName != null && b.lastName != null) {
            textB = (`${b.firstName} ${b.lastName}`).toUpperCase();
        } else {
            textB = b.username.toUpperCase();
        }

        if (a.firstName == null || a.lastName == null) {
            if (b.firstName != null && b.lastName != null) {
                sortValue = 1;
            }
        } else if (b.firstName == null || b.lastName == null) {
            sortValue = -1;
        }

        if (sortValue == null) {
            sortValue = (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        }

        return sortValue;
    }

    /**
     * Formatter for the input and result values for the autocomplete component
     * @param input {@link UserDetailModel} the value of the input
     * @return string a composed string from the input value
     */
    private resultAndInputFormatter(input: UserDetailModel): string {
        let result = null;
        if (input?.firstName != null && input?.lastName != null) {
            result = `${input?.firstName} ${input?.lastName} (${input?.username})`;
        } else {
            result = input?.username;
        }
        return result;
    };

    private getUserProxies(): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.userPncApi.getUserProxies()
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe((result) => {
                            const mappedAndReorderProxies = UserDetailMapper.fromUserDetailContractArray(result.proxies)
                                    ?.sort(this.chipSortFunction);
                            this.storeState({
                                currentProxies: mappedAndReorderProxies,
                                initialProxies: mappedAndReorderProxies,
                                partialProxyDetailInfo: result.partialInfo
                            });
                            this.proxyInputConfiguration = {
                                ...this.proxyInputConfiguration,
                                initialChips: this.state.currentProxies
                            };
                        }));
    }

    private proxiesChanged(): boolean {
        return this.state.currentProxies?.length !== this.state.initialProxies?.length
                || this.state.currentProxies.some((proxy) =>
                        this.state.initialProxies.find((entry) => entry.username === proxy.username) == null);
    }

}
